import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { useProviders, useActivateService } from '@/api/service-provisioning';
import { trackServiceActivate } from '@/lib/analytics-events';
import type { ServiceProduct } from './categories';
import { cdnConsoleRoute } from './cdn/cdn-activate';
import { kubernetesConsoleRoute } from './kubernetes/kubernetes-activate';
import { computeConsoleRoute } from './compute/compute-activate';
import { liveConsoleRoute, vodConsoleRoute } from './streaming/streaming-activate';

// Where to land after a successful activation, keyed by the provider codename the
// product activates on. The activation API returns only the new service id (no
// scope), so the console is resolved from the activation provider — NOT the
// catalog scope: Multi-CDN is catalogued as `owl` but lives at the CDN console
// after activating `raven`, so scope-based routing would send it to the wrong page.
const CONSOLE_ROUTE: Record<string, (serviceId: string) => string> = {
  raven: cdnConsoleRoute, // Multi-CDN (vEdge)
  wolf: kubernetesConsoleRoute, // Managed Kubernetes
  panda: computeConsoleRoute, // Cloud Compute
  penguin: liveConsoleRoute, // Live Streaming
  lens: vodConsoleRoute, // Media Service (VOD)
};

interface CatalogActivateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Catalog product being activated — drives the copy, provider and destination. */
  product: ServiceProduct;
}

/**
 * Generic self-serve activation dialog for the catalog. Activates the product's
 * own `activateProvider` (the backend mints the tenant) and routes to that
 * service's console. Replaces the CDN-specific dialog that was rendered for every
 * self-serve product — so each catalog product activates with its own identity
 * and provider instead of always provisioning Multi-CDN.
 */
export function CatalogActivateDialog({ open, onOpenChange, product }: CatalogActivateDialogProps) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();
  const { data: providers = [], isLoading: providersLoading } = useProviders();

  const providerCodename = product.activateProvider;

  // Only an active provider can be provisioned self-serve.
  const provider = useMemo(
    () =>
      providers.find(
        (p) => p.name?.trim().toLowerCase() === providerCodename && p.status === 'active',
      ),
    [providers, providerCodename],
  );

  const activate = useActivateService();
  const [serviceName, setServiceName] = useState<string>(product.displayName);

  const email = user?.email ?? '';
  const canSubmit = !!provider && !!serviceName.trim() && !!email && !activate.isPending;

  function handleSubmit() {
    if (!provider || !canSubmit) return;
    activate.mutate(
      { serviceName: serviceName.trim(), providerId: provider.id, email },
      {
        onSuccess: (service) => {
          trackServiceActivate(providerCodename ?? '', product.scope);
          show({
            tone: 'success',
            title: `${product.displayName} activated`,
            description: serviceName.trim(),
          });
          onOpenChange(false);
          const route =
            (providerCodename && CONSOLE_ROUTE[providerCodename]?.(service.id)) || '/services';
          navigate(route);
        },
        onError: (err) => {
          show({
            tone: 'danger',
            title: 'Activation failed',
            description: err.message || `Could not activate ${product.displayName}. Please try again.`,
          });
        },
      },
    );
  }

  const unavailable = !providersLoading && !provider;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Activate {product.displayName}</DialogTitle>
          <DialogDescription>{product.description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {providersLoading ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Skeleton style={{ height: 64 }} />
            </div>
          ) : unavailable ? (
            <p style={{ color: 'var(--vn-fg-muted)', margin: 0 }}>
              {product.displayName} is not available for self-serve activation on your account yet.
              Please contact sales to enable it.
            </p>
          ) : (
            <form
              id="catalog-activate-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
            >
              <Field
                label="Service name"
                description="Resources are provisioned for you automatically."
              >
                {(props) => (
                  <Input
                    {...props}
                    value={serviceName}
                    onChange={(e) => setServiceName(e.target.value)}
                    placeholder={product.displayName}
                  />
                )}
              </Field>
            </form>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!unavailable && (
            <Button
              type="submit"
              form="catalog-activate-form"
              disabled={!canSubmit}
              startIcon={<Sparkles size={16} />}
            >
              {activate.isPending ? 'Activating…' : 'Activate'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CatalogActivateDialog;
